import { useCallback, useRef, useState } from 'react';
import { fetchData } from '../../helpers/apiUtils';

/** Spiegel von `node-app/src/services/webinarUi/feldpruefung.ts` `FeldBefund`. */
export interface FeldBefund {
  schwere: 'ok' | 'hinweis' | 'warnung';
  text: string;
  quelle: 'regel' | 'ki';
  code?: string;
  stelle?: string;
}

/** Spiegel von `node-app/src/services/webinarUi/beispiele.ts` `Beispiel`. */
export interface Beispiel {
  wert: string;
  webinarId: string;
  titel: string;
  datum: string | null;
  produkt: string | null;
  gleichesProdukt: boolean;
}

export async function ladeBeispiele(feld: string, webinarId: string, limit = 5) {
  const q = new URLSearchParams({ feld, webinarId, limit: String(limit) });
  return fetchData<Beispiel[]>(`/api/webinar-management/beispiele?${q.toString()}`);
}

export async function pruefeFeldWert(
  feld: string,
  webinarId: string,
  wert: string,
  mitKi: boolean,
) {
  return fetchData<{ befunde: FeldBefund[] }>('/api/webinar-management/feldpruefung', {
    method: 'POST',
    body: JSON.stringify({ feld, webinarId, wert, mitKi }),
  });
}

/**
 * Prüft einen Feldwert beim Verlassen des Feldes (Regeln sofort, KI auf Wunsch). Hält nur das Ergebnis der
 * JÜNGSTEN Anfrage — eine langsame ältere Antwort überschreibt keine neuere. Gleicher Wert = keine neue Anfrage.
 * Ergebnis ist ein Hinweis; gespeichert oder gesperrt wird hier nichts.
 */
export function useFeldpruefung(feld: string, webinarId: string | null | undefined) {
  const [befunde, setBefunde] = useState<FeldBefund[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const zaehler = useRef(0);
  const letzter = useRef<string | null>(null);

  const pruefen = useCallback(
    async (wert: string, opts: { mitKi?: boolean } = {}) => {
      const schluessel = `${opts.mitKi ? 'ki' : 'regel'}\n${wert}`;
      if (!webinarId || letzter.current === schluessel) return;
      letzter.current = schluessel;
      if (!wert.trim()) {
        zaehler.current += 1;
        setBefunde([]);
        return;
      }
      const meine = ++zaehler.current;
      setLaeuft(true);
      const r = await pruefeFeldWert(feld, webinarId, wert, !!opts.mitKi);
      if (meine !== zaehler.current) return;
      setLaeuft(false);
      setBefunde(r.success && r.data ? r.data.befunde : []);
    },
    [feld, webinarId],
  );

  const zuruecksetzen = useCallback(() => {
    zaehler.current += 1;
    letzter.current = null;
    setBefunde([]);
    setLaeuft(false);
  }, []);

  return { befunde, laeuft, pruefen, zuruecksetzen };
}
